import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator as CalculatorIcon, Scale, Truck, TriangleAlert } from 'lucide-react';
import { categories } from '../data/products';
import { getTonsPerYard } from '../utils/loadSplit';
import { useSEO, JsonLd } from '../utils/seo';
type Row = {
  name: string;
  slug: string;
  categoryName: string;
  tonsPerYard: number;
  pricePerYard: number;
};
// Densities come from getTonsPerYard — the same function that sizes a
// truckload and states what a delivery weighs. Gravel, crushed rock, and
// drain rock all convert at the flat 1.3 tons/yd we quote and buy against;
// light organics and large quarry rock use their own published weights,
// which differ too much from 1.3 to share a factor. Reading the shared
// helper rather than re-parsing the spec copy is what keeps this chart, the
// load caps, and the cart from ever disagreeing.
const rows: Row[] = categories.flatMap(category => category.products.map(product => ({
  name: product.name,
  slug: product.slug,
  categoryName: category.name,
  tonsPerYard: getTonsPerYard(product.name),
  pricePerYard: product.pricePerYard
})));
const grouped = categories.map(category => ({
  name: category.name,
  rows: rows.filter(row => row.categoryName === category.name)
})).filter(group => group.rows.length > 0);
function lbsPerYard(tons: number): number {
  return Math.round(tons * 2000);
}
function yardsPerTon(tons: number): number {
  return 1 / tons;
}
const faqs = [{
  q: 'How many tons are in a cubic yard of gravel?',
  a: 'For gravel, crushed rock and drain rock we work at 1.3 tons per cubic yard — about 2,600 lbs. Lighter materials weigh far less: bark runs about 0.25 tons per yard and topsoil about 1.1, while large quarry rock runs heavier at roughly 1.5. Weight varies with moisture, so a load of anything after a week of rain will scale heavier than the same material in August.'
}, {
  q: 'How do I convert tons to cubic yards?',
  a: 'Divide the tons by the material\'s tons-per-yard figure. If a quarry quoted you 10 tons of 5/8" minus at 1.3 tons per yard, that is about 7.7 cubic yards. Going the other direction, multiply yards by tons per yard.'
}, {
  q: 'Why do quarries quote tons when 206Gravel sells cubic yards?',
  a: 'Quarries sell across a certified truck scale, so tons is what they can measure and bill exactly. Yards is what a project actually needs — you measure a driveway in feet and inches, not pounds. We quote yards because it is the unit you can check against your own tape measure, and we absorb the conversion on our end.'
}, {
  q: 'Is a cubic yard price cheaper or more expensive than a per-ton price?',
  a: 'Neither on its own — they are different units, so the numbers are not comparable until you convert. A $30 per ton quarry price on a 1.3-ton material is about $39 per cubic yard at the pit, before you have a truck, fuel, driver, or a way to get it home. Our per-yard price is delivered to your driveway.'
}, {
  q: 'How much does a cubic yard of gravel weigh?',
  a: 'About 2,600 lbs for crushed minus, clear rock and drain rock, roughly 2,200 lbs for topsoil, and about 500 lbs for bark. Large quarry rock is heavier still. That is why a full 12-yard load of rock is heavy enough to matter for driveway access, while 12 yards of bark is not.'
}];
export function TonsToYards() {
  useSEO({
    title: 'Tons to Cubic Yards — Washington Aggregate Conversion Chart | 206Gravel',
    description: 'Convert tons to cubic yards for every Puget Sound gravel, rock, sand, soil and bark material. Real per-yard weights, a live converter, and why quarries quote tons while we sell yards.',
    path: '/tons-to-yards'
  });
  const [amount, setAmount] = useState('10');
  const [unit, setUnit] = useState<'tons' | 'yards'>('tons');
  const [slug, setSlug] = useState(rows[0]?.slug ?? '');
  const selected = useMemo(() => rows.find(row => row.slug === slug) ?? rows[0], [slug]);
  const parsed = Number(amount);
  const valid = Number.isFinite(parsed) && parsed > 0 && Boolean(selected);
  const converted = valid && selected ? unit === 'tons' ? parsed * yardsPerTon(selected.tonsPerYard) : parsed * selected.tonsPerYard : null;
  return <div className="pt-24 pb-16 bg-[#F2EFE8] dark:bg-[#222222] min-h-screen" data-id="element-4241" data-mp="tfc3fpupkeoh">
      <JsonLd data={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    }} data-id="element-4242" data-mp="og8w1yr99wdc" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4243" data-mp="1c4s4ceab295f">
        <header className="max-w-3xl" data-id="element-4244" data-mp="1c37cwxjaf6bs">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#05894e] dark:text-[#4ed491]" data-id="element-4245" data-mp="1767cb257ivs2">
            Puget Sound conversion chart
          </p>
          <h1 className="mt-2 font-heading text-4xl md:text-5xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-4246" data-mp="nserqfce8f3">
            Tons to cubic yards
          </h1>
          <p className="mt-4 text-base md:text-lg leading-relaxed text-brand-muted dark:text-stone-300" data-id="element-4247" data-mp="3htsut3du7iw4">
            Every quarry in Washington quotes by the ton, because that is what
            their scale measures. Every project is measured in yards, because
            that is what a tape measure gives you. Here is the conversion for
            each material we haul, using the same densities on our product
            pages.
          </p>
        </header>

        {/* The converter comes before the chart. Most people arrive holding a
            specific number from a quarry ("they said 10 tons") and want it
            translated — reading a 30-row table to find their material is the
            slow path to the same answer. */}
        <section aria-labelledby="converter-heading" className="mt-10 rounded-2xl border border-stone-200 bg-white/85 p-6 shadow-sm sm:p-8 dark:border-white/10 dark:bg-[#2a2a2a]" data-id="element-4248" data-mp="2g35kptbu0l52">
          <div className="flex items-start gap-3" data-id="element-4249" data-mp="2l3tm98xoskdj">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#04A05C]/10 text-[#05894e] dark:bg-[#04A05C]/15 dark:text-[#4ed491]" data-id="element-4250" data-mp="2d2c6hojrturd">
              <Scale className="h-5 w-5" aria-hidden="true" data-id="element-4251" data-mp="2mz76yczlu58a" />
            </span>
            <div data-id="element-4252" data-mp="2mx47718ngxqd">
              <h2 id="converter-heading" className="font-heading text-2xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-4253" data-mp="2i0nfxy65m392">
                Convert your number
              </h2>
              <p className="mt-1 text-sm text-brand-muted dark:text-stone-400" data-id="element-4254" data-mp="2r1btysvjs3ly">
                Enter what you were quoted and pick the material.
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-[7rem_11rem_1fr]" data-id="element-4255" data-mp="30r57hzf92ssu">
            <div data-id="element-4256" data-mp="30syczbe4n6z9">
              <label htmlFor="convert-amount" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-muted dark:text-stone-400" data-id="element-4257" data-mp="3eqmr80dovfvc">
                Amount
              </label>
              <input id="convert-amount" type="text" inputMode="decimal" value={amount} onChange={event => setAmount(event.target.value.replace(/[^0-9.]/g, ''))} className="w-full rounded-lg border-2 border-stone-300 bg-white px-3 py-2.5 font-bold tabular-nums text-brand-charcoal focus:border-[#04A05C] focus:outline-none dark:border-white/15 dark:bg-[#1f1f1f] dark:text-white" data-id="element-4258" data-mp="3tdbwkiumgn9n" />
            </div>
            <div data-id="element-4259" data-mp="3amu9atsk6jvo">
              <label htmlFor="convert-unit" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-muted dark:text-stone-400" data-id="element-4260" data-mp="3amc2j389sn9i">
                Unit
              </label>
              <select id="convert-unit" value={unit} onChange={event => setUnit(event.target.value as 'tons' | 'yards')} className="w-full rounded-lg border-2 border-stone-300 bg-white px-3 py-2.5 font-bold text-brand-charcoal focus:border-[#04A05C] focus:outline-none dark:border-white/15 dark:bg-[#1f1f1f] dark:text-white" data-id="element-4261" data-mp="31mpcteowl5wa">
                <option value="tons" data-id="element-4262" data-mp="2wm1ee7ychms1">Tons</option>
                <option value="yards" data-id="element-4263" data-mp="2woxd3yi0ofhj">Cubic yards</option>
              </select>
            </div>
            <div data-id="element-4264" data-mp="3c2q5rzunqwz5">
              <label htmlFor="convert-material" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-muted dark:text-stone-400" data-id="element-4265" data-mp="37698af1kj69e">
                Material
              </label>
              <select id="convert-material" value={slug} onChange={event => setSlug(event.target.value)} className="w-full rounded-lg border-2 border-stone-300 bg-white px-3 py-2.5 font-bold text-brand-charcoal focus:border-[#04A05C] focus:outline-none dark:border-white/15 dark:bg-[#1f1f1f] dark:text-white" data-id="element-4266" data-mp="2y5l0i22rq24y">
                {grouped.map(group => <optgroup key={group.name} label={group.name} data-id="element-4267" data-mp="2jh84x67lelaz">
                    {group.rows.map(row => <option key={row.slug} value={row.slug} data-id="element-4268" data-mp="2ffuzgx8ewk4l">
                        {row.name}
                      </option>)}
                  </optgroup>)}
              </select>
            </div>
          </div>

          <div className="mt-6 border-t border-stone-200 pt-5 dark:border-white/10" aria-live="polite" data-id="element-4269" data-mp="25hyoc5fh5p6h">
            {valid && converted !== null && selected ? <>
                <p className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white sm:text-4xl" data-id="element-4270" data-mp="px6guazdssgh">
                  {converted.toLocaleString(undefined, {
                maximumFractionDigits: 1
              })}{' '}
                  <span className="text-xl font-bold text-brand-muted dark:text-stone-400" data-id="element-4271" data-mp="yxumahaplkhr">
                    {unit === 'tons' ? 'cubic yards' : 'tons'}
                  </span>
                </p>
                <p className="mt-2 text-sm text-brand-muted dark:text-stone-400" data-id="element-4272" data-mp="77q2okurx1hl">
                  {parsed.toLocaleString(undefined, {
                maximumFractionDigits: 2
              })}{' '}
                  {unit === 'tons' ? 'tons' : 'cubic yards'} of {selected.name}{' '}
                  at {lbsPerYard(selected.tonsPerYard).toLocaleString()} lbs
                  per cubic yard.
                  {/* Rounding matters here: we bill whole yards, and a
                      truck cannot deliver 7.1 of anything. Saying so up
                      front avoids an argument at the driveway. */}
                  {unit === 'tons' && <>
                      {' '}
                      We deliver in whole yards, so this would be ordered as{' '}
                      <span className="font-bold text-brand-charcoal dark:text-white" data-id="element-4273" data-mp="26951kpwqsq0u">
                        {Math.max(3, Math.ceil(converted))} yards
                      </span>
                      .
                    </>}
                </p>
                <div className="mt-5 flex flex-wrap gap-3" data-id="element-4274" data-mp="1dkhagdpbb6fd">
                  <Link to={`/shop/${selected.slug}`} className="inline-flex items-center gap-2 rounded-full bg-brand-button px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-buttonHover" data-id="element-4275" data-mp="1ij2eypblkc9m">
                    See {selected.name} delivered price
                    <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-4276" data-mp="utge7xfktozi" />
                  </Link>
                  <Link to="/calculator" className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-5 py-2.5 text-sm font-bold text-brand-charcoal transition-colors hover:border-[#04A05C] hover:text-[#05894e] dark:border-white/15 dark:text-white dark:hover:text-[#4ed491]" data-id="element-4277" data-mp="na77kbygg4e5">
                    <CalculatorIcon className="h-4 w-4" aria-hidden="true" data-id="element-4278" data-mp="9afv4fnl6w29" />
                    Work out yards from area
                  </Link>
                </div>
              </> : <p className="text-sm text-brand-muted dark:text-stone-400" data-id="element-4279" data-mp="4cnvt0km47o1">
                Enter an amount to see the conversion.
              </p>}
          </div>
        </section>

        {/* The honest framing. Someone comparing a $30/ton quarry number to a
            $65/yard delivered number is comparing two different things, and
            if we don't do that math for them, they'll assume we're the
            expensive one. */}
        <section aria-labelledby="pickup-heading" className="mt-12 grid gap-4 md:grid-cols-2" data-id="element-4280" data-mp="3crefyryuqohs">
          <div className="rounded-2xl border border-stone-200 bg-white p-6 dark:border-white/10 dark:bg-[#2a2a2a]" data-id="element-4281" data-mp="333sf9stzllax">
            <h2 id="pickup-heading" className="font-heading text-xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-4282" data-mp="375dz5y4xuzum">
              A per-ton quarry price is not a per-yard price
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-brand-muted dark:text-stone-300" data-id="element-4283" data-mp="2y5imtpzncyt0">
              Say a pit quotes $30 per ton for 5/8" minus. That material runs
              about 1.4 tons per cubic yard, so the same yard costs roughly{' '}
              <span className="font-bold text-brand-charcoal dark:text-white" data-id="element-4284" data-mp="3c48fmtffocbk">
                $42 at the scale house
              </span>
              . Then you still need a truck rated for 2,800 lbs per yard, the
              fuel, the round trip, and somewhere to put it while you unload by
              hand. The per-ton number is real; it just is not the number you
              pay to have material in your driveway.
            </p>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-6 dark:border-white/10 dark:bg-[#2a2a2a]" data-id="element-4285" data-mp="1y32m3xvuwl4o">
            <h3 className="font-heading text-xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-4286" data-mp="1t3gafsvofcxz">
              What we quote instead
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-brand-muted dark:text-stone-300" data-id="element-4287" data-mp="22wnbx2naq8ss">
              One delivered price per cubic yard, plus one flat haul fee per
              truckload for your zone, with Washington sales tax figured at
              your delivery address. Nothing gets added at the driveway. You
              can check the yardage against your own measurements, which is the
              whole reason we bill in yards rather than tons.
            </p>
            <Link to="/delivery-zones" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#05894e] hover:underline dark:text-[#4ed491]" data-id="element-4288" data-mp="2zktm052zlteg">
              See haul fees by zone
              <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-4289" data-mp="3pxhwcn2fdgag" />
            </Link>
          </div>
        </section>

        <section aria-labelledby="chart-heading" className="mt-12" data-id="element-4290" data-mp="3uwkqvdks4446">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-4291" data-mp="3oyt21kqqvv8">
            <h2 id="chart-heading" className="font-heading text-2xl md:text-3xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-4292" data-mp="8obfil7pa0xa">
              Weight and conversion by material
            </h2>
            <p className="text-sm text-brand-muted dark:text-stone-400" data-id="element-4293" data-mp="hordjf5ocfur">
              {rows.length} materials · {new Date().getFullYear()} densities
            </p>
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-brand-muted dark:text-stone-300" data-id="element-4294" data-mp="rf2t5yyq0dhh">
            Densities are dry-weight figures for material as we load it. Wet
            material scales heavier, which is why a quarry ticket in February
            can read 10% high against these numbers for the same volume.
          </p>

          <div className="mt-6 overflow-hidden rounded-2xl border border-stone-200 bg-white/85 shadow-sm dark:border-white/10 dark:bg-[#2a2a2a]" data-id="element-4295" data-mp="2kykyjb9gjdww">
            <div className="overflow-x-auto" data-id="element-4296" data-mp="2bx3nw8etk4fl">
              <table className="min-w-[760px] w-full text-left border-collapse" data-id="element-4297" data-mp="2bxwlairw1hl2">
                <caption className="sr-only" data-id="element-4298" data-mp="26ytrjuhcyxse">
                  Weight per cubic yard, tons per cubic yard, cubic yards per
                  ton, and delivered price per cubic yard for every 206Gravel
                  material
                </caption>
                <thead data-id="element-4299" data-mp="2zosijvf454zp">
                  <tr className="border-b border-stone-200 bg-stone-50 dark:border-white/10 dark:bg-white/5" data-id="element-4300" data-mp="2uvpqxstwc5m5">
                    <th scope="col" className="p-4 font-bold text-brand-muted" data-id="element-4301" data-mp="2pxegbfvs1r2v">
                      Material
                    </th>
                    <th scope="col" className="p-4 font-bold text-brand-muted" data-id="element-4302" data-mp="3nduymyu3mps3">
                      Lbs / yard
                    </th>
                    <th scope="col" className="p-4 font-bold text-brand-muted" data-id="element-4303" data-mp="1go019wi30f5r">
                      Tons / yard
                    </th>
                    <th scope="col" className="p-4 font-bold text-brand-muted" data-id="element-4304" data-mp="1kojwuvn0baps">
                      Yards / ton
                    </th>
                    <th scope="col" className="p-4 font-bold text-brand-muted" data-id="element-4305" data-mp="16w43ynntkt2l">
                      Price / yard
                    </th>
                  </tr>
                </thead>
                {grouped.map(group => <tbody key={group.name} className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-4306" data-mp="x2dh1n7ccqp0">
                    <tr className="bg-stone-50/70 dark:bg-white/[0.03]" data-id="element-4307" data-mp="11wr6l8zbo3lr">
                      <th scope="colgroup" colSpan={5} className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#05894e] dark:text-[#4ed491]" data-id="element-4308" data-mp="o16xf59eazrj">
                        {group.name}
                      </th>
                    </tr>
                    {group.rows.map(row => <tr key={row.slug} data-id="element-4309" data-mp="2nbf3rnczp6bo">
                        <td className="p-4" data-id="element-4310" data-mp="2na40dongi0l3">
                          <Link to={`/shop/${row.slug}`} className="font-bold text-brand-charcoal hover:text-[#05894e] hover:underline dark:text-white dark:hover:text-[#4ed491]" data-id="element-4311" data-mp="2xflnebetzk1c">
                            {row.name}
                          </Link>
                        </td>
                        <td className="p-4 text-sm font-medium tabular-nums text-brand-muted dark:text-stone-300" data-id="element-4312" data-mp="31i8jwpmg5t4v">
                          {lbsPerYard(row.tonsPerYard).toLocaleString()}
                        </td>
                        <td className="p-4 text-sm font-medium tabular-nums text-brand-muted dark:text-stone-300" data-id="element-4313" data-mp="3b8l42gqcnkah">
                          {row.tonsPerYard.toFixed(2)}
                        </td>
                        <td className="p-4 text-sm font-medium tabular-nums text-brand-muted dark:text-stone-300" data-id="element-4314" data-mp="3l5puisr8a1x3">
                          {yardsPerTon(row.tonsPerYard).toFixed(2)}
                        </td>
                        <td className="p-4 text-sm font-bold tabular-nums text-brand-charcoal dark:text-white" data-id="element-4315" data-mp="1qszwpzxqo8kf">
                          ${row.pricePerYard}
                        </td>
                      </tr>)}
                  </tbody>)}
              </table>
            </div>
          </div>
        </section>

        <section aria-labelledby="weight-heading" className="mt-12 rounded-2xl border border-[#CB6E0A]/30 bg-[#CB6E0A]/[0.07] p-6 dark:border-[#F6A93B]/30 dark:bg-[#F6A93B]/[0.07]" data-id="element-4316" data-mp="2p3zmaiy9xctg">
          <div className="flex items-start gap-3" data-id="element-4317" data-mp="1wf2exavlp0fh">
            <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#CB6E0A] dark:text-[#F6A93B]" aria-hidden="true" data-id="element-4318" data-mp="1wgdhx8h8245j" />
            <div data-id="element-4319" data-mp="25eyv0o3xcqyx">
              <h2 id="weight-heading" className="font-heading text-xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-4320" data-mp="25efmtz42tgy3">
                Weight is why load size is capped
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-stone-700 dark:text-white/85" data-id="element-4321" data-mp="3gsuc42ee39ss">
                A truck is limited by weight, not volume. Our legal payload is
                15.5 tons, so a standard load is 12 yards of gravel or crushed
                rock, which puts a full truck at or near that limit. Bark and
                topsoil
                are light enough to go 15 yards, and large quarry rock is heavy
                enough that a load caps below 12. If you were quoted tons and
                the number converts to more than one load, it is more than one
                delivery, and the haul fee applies per load.
              </p>
              <Link to="/delivery-prep" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#05894e] hover:underline dark:text-[#4ed491]" data-id="element-4322" data-mp="387kug0wq1r0z">
                <Truck className="h-4 w-4" aria-hidden="true" data-id="element-4323" data-mp="338i13dq3t98u" />
                Check your site can take the load
              </Link>
            </div>
          </div>
        </section>

        <section aria-labelledby="faq-heading" className="mt-12" data-id="element-4324" data-mp="11gbnhi5ma1s0">
          <h2 id="faq-heading" className="font-heading text-2xl md:text-3xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-4325" data-mp="xeqcy3gk1zkz">
            Common questions
          </h2>
          <dl className="mt-6 space-y-4" data-id="element-4326" data-mp="2w53udxr7v4rn">
            {faqs.map(faq => <div key={faq.q} className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-white/10 dark:bg-[#2a2a2a]" data-id="element-4327" data-mp="2w5nd3gooxx6q">
                <dt className="font-bold text-brand-charcoal dark:text-white" data-id="element-4328" data-mp="2mfal53ejr1qk">
                  {faq.q}
                </dt>
                <dd className="mt-2 text-sm leading-relaxed text-brand-muted dark:text-stone-300" data-id="element-4329" data-mp="2hh0qctwqi35h">
                  {faq.a}
                </dd>
              </div>)}
          </dl>
        </section>

        <section className="mt-12 flex flex-col items-start justify-between gap-5 rounded-2xl border border-stone-200 bg-white px-6 py-6 sm:flex-row sm:items-center dark:border-white/10 dark:bg-[#2a2a2a]" data-id="element-4330" data-mp="28ehs6n3w8epk">
          <div data-id="element-4331" data-mp="6mv7f3alhg1j">
            <p className="font-heading text-xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-4332" data-mp="6neetq2mcm1b">
              Know your yards? Get the delivered total.
            </p>
            <p className="mt-1 text-sm text-brand-muted dark:text-stone-400" data-id="element-4333" data-mp="9948sg2oy1wu">
              Material, haul, and tax for your address — before you order.
            </p>
          </div>
          <Link to="/#materials" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-button px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-buttonHover" data-id="element-4334" data-mp="ixm62nbrhqvq">
            Browse materials
            <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-4335" data-mp="3mg5zvua394sn" />
          </Link>
        </section>
      </div>
    </div>;
}